import { useCallback, useEffect, useState } from "react";
import { Download, LogOut, Loader2, Users } from "lucide-react";
import { fetchSignups, exportSignups, formatApiError, fetchLaunch, sendLaunch, fetchSneakPeek, sendSneakPeek } from "../../lib/api";
import { Logo } from "../woven/Logo";
import { CampaignPanel } from "./CampaignPanel";
import { ScreenshotManager } from "./ScreenshotManager";
import { StoreLinksPanel } from "./StoreLinksPanel";
import { StoriesPanel } from "./StoriesPanel";
import { EmailQueueNote } from "./EmailQueueNote";

const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });

const SignupsTable = ({ signups }) => (
  <div className="warm-card mt-8 overflow-hidden">
    <div className="overflow-x-auto">
      <table data-testid="admin-signups-table" className="w-full min-w-[480px] text-left">
        <thead className="bg-[#F7F2E9] text-xs uppercase tracking-wider text-[#8C6D58]">
          <tr><th className="px-6 py-4 font-semibold">#</th><th className="px-6 py-4 font-semibold">Email</th><th className="px-6 py-4 font-semibold">Signed up</th></tr>
        </thead>
        <tbody>
          {signups.length === 0 && (
            <tr><td colSpan={3} data-testid="admin-empty-state" className="px-6 py-12 text-center text-[#8A7A72]">No signups yet.</td></tr>
          )}
          {signups.map((s, i) => (
            <tr key={s.id} data-testid="admin-signup-row" className="border-t border-[#E6DEC6]/70 hover:bg-[#FDFBF7] transition-colors">
              <td className="px-6 py-4 text-sm text-[#8A7A72]">{signups.length - i}</td>
              <td className="px-6 py-4 text-[#2C221E] break-all">
                {s.email}
                {s.invited_by && <span data-testid="admin-invited-by" className="mt-1 block text-xs text-[#536B5B]">Invited by {s.invited_by}</span>}
                {s.referred_by && <span data-testid="admin-referred-by" className="mt-1 block text-xs text-[#B9823F]">Shared by {s.referred_by}</span>}
              </td>
              <td className="px-6 py-4 text-sm text-[#5C4F4A] whitespace-nowrap">{fmt(s.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const AdminDashboard = ({ token, onLogout }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const handleErr = useCallback((err) => {
    if (err.response?.status === 401) return onLogout();
    setError(formatApiError(err.response?.data?.detail));
  }, [onLogout]);

  useEffect(() => {
    fetchSignups(token).then(({ data }) => setData(data)).catch(handleErr);
  }, [token, handleErr]);

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await exportSignups(token);
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `woven-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      handleErr(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#E6DEC6] bg-[#FDFBF7]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 sm:px-8 py-5">
          <Logo />
          <button onClick={onLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm text-[#5C4F4A] hover:text-[#C85A32] transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 sm:px-8 py-12">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow">Waitlist</p>
            <h1 className="mt-2 font-serif text-4xl text-[#2C221E]">Signups</h1>
          </div>
          <button onClick={onExport} disabled={!data || exporting} data-testid="admin-export-csv-button" className="btn-primary">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
          </button>
        </div>
        <div className="warm-card mt-8 flex items-center gap-5 p-6 sm:max-w-xs">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F8EFEA] text-[#C85A32]"><Users className="h-5 w-5" /></span>
          <div>
            <p className="text-sm text-[#8A7A72]">Total signups</p>
            <p data-testid="admin-total-count" className="font-serif text-3xl text-[#2C221E]">{data ? data.total : "—"}</p>
          </div>
        </div>
        {error && <p role="alert" data-testid="admin-error" className="mt-6 text-sm text-[#B24E2A]">{error}</p>}
        <EmailQueueNote token={token} />
        {data ? <SignupsTable signups={data.signups} /> : !error && <p className="mt-8 text-[#8A7A72]">Loading…</p>}
        <StoriesPanel token={token} onUnauthorized={onLogout} />
        <StoreLinksPanel token={token} onUnauthorized={onLogout} />
        <CampaignPanel token={token} onUnauthorized={onLogout} prefix="sneak" eyebrow="Sneak peek email" title="A first look before Christmas"
          sendLabel="Send sneak peek" fetcher={fetchSneakPeek} sender={sendSneakPeek}>
          {(info, reload) => <ScreenshotManager token={token} shots={info.screenshots} onChange={reload} onUnauthorized={onLogout} />}
        </CampaignPanel>
        <CampaignPanel token={token} onUnauthorized={onLogout} prefix="launch" eyebrow="Launch email" title="Tell everyone Woven is ready"
          sendLabel="Send launch email" fetcher={fetchLaunch} sender={sendLaunch} />
      </main>
    </div>
  );
};
